import { SW_VERSION } from '@/sw/version';
import type { VersionInfo } from '@/sw/protocol';
import { toast } from 'sonner';
import { getWaitingVersionInfo } from './swBridge';

/**
 * Window half of the SW update flow (see src/sw/index.ts for the SW half).
 *
 * - UPDATE_READY (broadcast after a sync leaves the queues empty and a new
 *   SW version is waiting): the update is safe to apply — a toast offers a
 *   restart; applying posts SKIP_WAITING to the waiting worker and reloads
 *   on controllerchange.
 * - PREFERRED_MINIMUM_CLIENT_VERSION (server force-upgrade channel, reported
 *   with sync results): when this build is older than the server's minimum,
 *   apply a waiting update immediately, or ask the browser to fetch one.
 */

/** Build version compiled into both the window and SW bundles. */
export const CLIENT_VERSION = SW_VERSION;

/** Numeric version segments; build metadata (+…) ignored. */
function versionSegments(version: string): number[] {
  return (version.split('+')[0]!.match(/\d+/g) ?? []).map(Number);
}

/**
 * Whether `current` is older than `minimum`. Versions without numeric
 * segments (e.g. the 'dev' fallback) are never outdated.
 */
export function isOutdated(current: string, minimum: string): boolean {
  const c = versionSegments(current);
  const m = versionSegments(minimum);
  if (c.length === 0 || m.length === 0) {
    return false;
  }
  for (let i = 0; i < Math.max(c.length, m.length); i += 1) {
    const a = c[i] ?? 0;
    const b = m[i] ?? 0;
    if (a !== b) {
      return a < b;
    }
  }
  return false;
}

async function getRegistration(): Promise<
  ServiceWorkerRegistration | undefined
> {
  if (!('serviceWorker' in navigator)) {
    return undefined;
  }
  return navigator.serviceWorker.getRegistration();
}

/**
 * Activate the waiting SW (if any) and reload when it takes control. The SW
 * only announces UPDATE_READY with drained queues, so activation is safe.
 * Returns whether a waiting worker was found.
 */
export async function applyWaitingUpdate(): Promise<boolean> {
  const registration = await getRegistration();
  const waiting = registration?.waiting;
  if (!waiting) {
    return false;
  }
  navigator.serviceWorker.addEventListener(
    'controllerchange',
    () => window.location.reload(),
    { once: true },
  );
  waiting.postMessage({ type: 'SKIP_WAITING' });
  return true;
}

let updatePromptShown = false;

/**
 * UPDATE_READY handler: offer a restart (never force one mid-work). The
 * waiting worker is asked for its version + changelog first (GET_VERSION
 * over a MessageChannel) so the toast can say what the update is; when the
 * waiting bundle carries release notes, `onShowChangelog` wires the
 * "What's new" button (OfflineManager renders the viewer dialog).
 */
export async function promptUpdateReady(
  onShowChangelog?: (info: VersionInfo) => void,
): Promise<void> {
  if (updatePromptShown) {
    return;
  }
  updatePromptShown = true;
  const info = await getWaitingVersionInfo();
  toast(
    info?.version && info.version !== 'dev'
      ? `Version ${info.version} is ready`
      : 'A new version is ready',
    {
      description: 'Restart to update — your offline changes are synced.',
      duration: Infinity,
      action: {
        label: 'Restart',
        onClick: () => void applyWaitingUpdate(),
      },
      ...(info?.changelog && onShowChangelog
        ? {
            cancel: {
              label: "What's new",
              onClick: () => onShowChangelog(info),
            },
          }
        : {}),
      onDismiss: () => {
        updatePromptShown = false;
      },
    },
  );
}

/**
 * PREFERRED_MINIMUM_CLIENT_VERSION handler. Outdated build → apply the
 * waiting update, or trigger an update check and apply once installed.
 * Event-driven (updatefound/statechange), no reload loops: without a newer
 * SW on the server this does nothing.
 */
export async function handleMinimumClientVersion(
  minimum: string,
): Promise<void> {
  if (!isOutdated(CLIENT_VERSION, minimum)) {
    return;
  }
  if (await applyWaitingUpdate()) {
    return;
  }
  const registration = await getRegistration();
  if (!registration) {
    return;
  }
  try {
    await registration.update();
  } catch {
    return; // offline or server unreachable — next sync reports again
  }
  if (await applyWaitingUpdate()) {
    return;
  }
  const installing = registration.installing;
  if (installing) {
    installing.addEventListener('statechange', () => {
      if (installing.state === 'installed') {
        void applyWaitingUpdate();
      }
    });
  }
}
